import Skeleton from 'react-loading-skeleton';
import { useLocation } from 'react-router';

import { useGetMainNumbersData, useGetPrettyNumbersData } from '@/api';
import { OperatorType } from '@/api/enums/Operators.ts';
import { GetReservationsResponse, NumbersResponse } from '@/api/types';
import { NumberBlock } from '@/components/AllNumbers/NumberBlock/NumberBlock.tsx';
import { appRoutes } from '@/routes/app-routes.ts';
import { ChosenNumber } from '@/types';
import { formatNumberToStr, getCorrectMultipleWord, getEnumKeyByValue } from '@/utils';

import cn from './styles.module.sass';

interface INumbersListProps {
    numsData?: Omit<NumbersResponse['data'], 'total'> | GetReservationsResponse['data'];
    onClick: (chosenNumber: ChosenNumber) => void;
    foundAmount?: number;
    isLoading?: boolean;
}

export const NumbersList = ({ numsData, onClick, foundAmount, isLoading }: INumbersListProps) => {
    const { data: mainTariffsData } = useGetMainNumbersData();
    const { pathname } = useLocation();
    const { data: prettyNumsTariffsData } = useGetPrettyNumbersData();

    const tariffsData = mainTariffsData || prettyNumsTariffsData;

    if (numsData && 'list' in numsData && numsData.list.length === 0 && !isLoading) {
        return <div className={cn.noNumbers}>Не найдено номеров</div>;
    }

    return (
        <div className={cn.numbersList}>
            {(pathname.includes(appRoutes.myReservations) ||
                pathname.includes(appRoutes.prettyNums)) && (
                <div className={cn.foundAmount}>
                    {isLoading || foundAmount === undefined ? (
                        <Skeleton width={200} />
                    ) : (
                        <>
                            Найдено: {foundAmount ?? 0}{' '}
                            {getCorrectMultipleWord('номер', foundAmount ?? 0)}
                        </>
                    )}
                </div>
            )}
            <div className={cn.numbersListContainer}>
                {!isLoading ? (
                    numsData && 'list' in numsData ? (
                        numsData.list.map(item => {
                            const numTariffs = tariffsData?.tariffs
                                .filter(t => item.tariffs.includes(t.sid))
                                .map(t => t.publicPrice + item.cost);

                            const minTariffPrice = numTariffs
                                ? Math.min.apply(null, numTariffs)
                                : 0;

                            const operator =
                                getEnumKeyByValue(OperatorType, item.operator.id) ?? '';
                            return (
                                <NumberBlock
                                    numberCost={item.cost}
                                    key={item.msisdn}
                                    onClick={onClick}
                                    operator={operator}
                                    number={item.msisdn}
                                    tariffMin={minTariffPrice}
                                    region={item.region.name}
                                    tariffs={item.tariffs}
                                    isMyReservations={pathname.includes(appRoutes.myReservations)}
                                />
                            );
                        })
                    ) : (
                        numsData?.map(item => {
                            const tariffsSids = item.tariffs.map(t => t.sid);
                            const operator =
                                getEnumKeyByValue(OperatorType, item.number.operator.id) ?? '';

                            return (
                                <NumberBlock
                                    numberCost={item.number.cost ?? 0}
                                    key={item.number.msisdn}
                                    onClick={onClick}
                                    operator={operator}
                                    number={formatNumberToStr(item.number.msisdn)}
                                    tariffMin={0}
                                    region={item.number.region.name}
                                    tariffs={tariffsSids}
                                    expiry={item.number.expiry}
                                    isMyReservations={pathname.includes(appRoutes.myReservations)}
                                />
                            );
                        })
                    )
                ) : (
                    <Skeleton
                        containerClassName={cn.skeletonContainer}
                        borderRadius={8}
                        height={49}
                        count={5}
                    />
                )}
            </div>
        </div>
    );
};
